import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../services/api"; // ✅ use centralized API

const ConstituencyProfilePage = () => {
  const { type, constituencyName } = useParams();
  const [rep, setRep] = useState(null);
  const [issueCount, setIssueCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRepresentative = async () => {
      try {
        const lastVisitedState = localStorage.getItem("lastStateVisited") || "Bihar";

        const { data } = await api.get(`/state/${lastVisitedState}`);

        const list = type?.toUpperCase() === "MP" ? data?.mps : data?.mlas;

        if (!Array.isArray(list)) {
          throw new Error(`No ${type} list found in state`);
        }

        const matched = list.find(
          (r) =>
            r.constituency?.toLowerCase().trim() ===
            decodeURIComponent(constituencyName).toLowerCase().trim()
        );

        if (!matched) {
          setRep(null);
          return;
        }

        setRep(matched);

        const countRes = await api.get(`/issues/count/${type}/${encodeURIComponent(matched.name)}`);
        setIssueCount(countRes.data?.count || 0);
      } catch (err) {
        console.error("❌ Failed to fetch representative or issue count:", err.message);
        toast.error("Failed to fetch representative data");
      } finally {
        setLoading(false);
      }
    };

    fetchRepresentative();
  }, [type, constituencyName]);

  if (loading) return <div className="text-center py-20 text-orange-500">Loading...</div>;

  if (!rep) {
    return (
      <div className="text-center text-red-500 py-20">
        No representative found for this constituency.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-white to-green-100 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 md:p-10">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <img
            src={rep.image_url || "/leader-default.png"}
            alt={rep.name}
            className="w-32 h-32 object-cover rounded-full border-2 border-green-500"
          />
          <div className="text-center md:text-left">
            <h1 className="text-3xl font-bold text-orange-600">
              {type} Of {rep.constituency}, {rep.district || "Unknown"}
            </h1>
            <p className="text-gray-700 mt-1 text-sm">
              <span className="font-semibold text-green-700">{rep.name}</span> from{" "}
              <span className="text-blue-700">{rep.party}</span>
            </p>
            <p className="text-sm text-gray-600 mt-1">
              <strong>Role:</strong> {rep.role || "Sitting"}
            </p>

            <p className="text-sm text-blue-600 mt-2 font-semibold">
              🛠️ {issueCount} Issues Raised Against
            </p>
            <Link
              to={`/candidate/${encodeURIComponent(rep.name)}`}
              className="mt-2 inline-block text-white bg-gradient-to-r from-orange-500 to-green-600 hover:from-orange-600 hover:to-green-700 px-4 py-2 rounded-lg shadow"
            >
              View Profile →
            </Link>
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate(`/raise-issue?to=${type.toLowerCase()}&name=${rep.name}`)}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-lg font-semibold shadow"
          >
            ✋ Raise Issue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConstituencyProfilePage;
